import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
import { Trans } from '@lingui/macro';
import isEmpty from 'lodash/isEmpty';
import isFunction from 'lodash/isFunction';
import merge from 'lodash/merge';
import pick from 'lodash/pick';
import { Button } from '@material-ui/core';
import AdminPage from '../AdminPage';
import Actions from '../../page-framework/actions/Actions';
import NgpForm from '../../form/NgpForm';
import { USE_FORM_DEFAULT_OPTIONS } from '../../../utilities/FormUtilities';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { useFindById } from '../../../services/FormQueryService';
import { useFormDataTableActionWrapper } from '../../../services/FormDataTableActionService';
import { usePrevious } from '../../../utilities/UsePrevious';
import { RecordContextProviderUtilities } from '../../../utilities/RecordContextProviderUtilities';
import { NgpFormContextProviderUtilities } from '../../../utilities/NgpFormContextProviderUtilities';
import useActioningContext, { ActioningProviderUtilities } from '../../../utilities/ActioningProviderUtilities';
import { useInfrastructureDetailActions } from './hooks/UseInfrastructureDetailActions';
import { useInfrastructureFormSubmit } from './hooks/UseInfrastructureFormSubmit';
import { useInfrastructureSaveError } from './hooks/UseInfrastructureSaveError';
import useInfrastructureBreadcrumb from './hooks/UseInfrastructureBreadcrumb';

/**
 * Base component for infrastructure and interface detail pages (internal, wrapped by ActioningProvider)
 * Uses composition pattern for flexibility
 *
 * @param {Object} props - Component props
 * @param {string} props.recordId - Current record ID
 * @param {boolean} props.tableSelectionAction - use ids or selection as action post body
 * @param {string} props.baseUrl - Base API URL
 * @param {string} props.goToUrl - Navigation URL after action
 * @param {Object} props.defaultValues - Form default values
 * @param {string} props.pageId - Page identifier
 * @param {string} props.nameField - Field name for breadcrumb
 * @param {string} props.typeField - Field name for record type
 * @param {string} props.newRecordBreadcrumbText - Text for new record breadcrumb
 * @param {Array} props.functionBreadcrumbs - Breadcrumb navigation array
 * @param {Object} props.actionUrls - Map of action names to URLs
 * @param {Function} props.createSubmitTransform - Transform function for create submissions
 * @param {Function} props.submitTransform - Transform applied on both create and update submissions
 * @param {Function} props.getTargetConfig - Function to get drill-down target config
 * @param {Function} props.isSaveDisabled - Function to determine if save is disabled
 * @param {Function} props.actionButtonsFn - Action button render function or element
 * @param {React.ReactNode} props.children - Form section children
 */
function InfrastructureFormDetailBase({
    recordId,
    tableSelectionAction,
    baseUrl,
    goToUrl,
    defaultValues,
    pageId,
    nameField,
    typeField,
    newRecordBreadcrumbText,
    functionBreadcrumbs,
    actionUrls,
    createSubmitTransform,
    submitTransform,
    getTargetConfig,
    isSaveDisabled,
    actionButtonsFn,
    showBreadcrumb,
    showSaveButton,
    showSave,
    setTestTypeId,
    children,
}) {
    const { isActioning, setIsActioning } = useActioningContext();
    const isCreateAction = isEmpty(recordId);
    const prevRecordId = usePrevious(recordId);
    const { dataTableActioning } = useFormDataTableActionWrapper();
    const { errorWrapper } = useErrorWrapper();
    const methods = useForm(USE_FORM_DEFAULT_OPTIONS);
    const { reset, setError, handleSubmit } = methods;
    const [showDataTableActionButton, setShowDataTableActionButton] = useState(false);

    const handleSaveError = useInfrastructureSaveError();

    const postFindByIdHandler = useCallback(
        (response) => {
            setShowDataTableActionButton(true);
            return merge(pick(defaultValues, []), response);
        },
        [defaultValues]
    );

    const { initialized, finding, formData, resetForm } = useFindById({
        url: baseUrl,
        recordId,
        postFindByIdHandler,
        resetFunc: reset,
        defaultValues,
        errorHandler: errorWrapper({}, goToUrl),
    });

    useEffect(() => {
        if (!isEmpty(recordId) && !isEmpty(prevRecordId) && recordId !== prevRecordId) {
            resetForm();
        }
    }, [recordId, prevRecordId, resetForm]);

    // Special for service test detail
    useEffect(() => {
        if (!setTestTypeId || !isFunction(setTestTypeId)) {
            return;
        }

        if (!formData) {
            return;
        }

        const testTypeId = formData?.type;
        if (!testTypeId) {
            return;
        }
        setTestTypeId(testTypeId);
    }, [setTestTypeId, formData]);

    const recordType = formData?.[typeField];
    const reportingLinkConfig = getTargetConfig ? getTargetConfig(recordType) : undefined;

    const breadcrumbs = useInfrastructureBreadcrumb(
        showBreadcrumb,
        functionBreadcrumbs,
        recordId,
        formData,
        nameField,
        newRecordBreadcrumbText,
        {
            reportingLinkConfig,
        }
    );

    const { handleConfirmedDataTableAction } = useInfrastructureDetailActions({
        recordId,
        tableSelectionAction,
        actionUrls,
        goToUrl,
        setIsActioning,
    });

    const { saving, handleSubmitFunction, dirtyCheck } = useInfrastructureFormSubmit({
        baseUrl,
        goToUrl,
        formData,
        setError,
        saveErrorHandler: handleSaveError,
        isCreateAction,
        createSubmitTransform,
        submitTransform,
    });

    const saveDisabled = isSaveDisabled
        ? isSaveDisabled({ saving, isActioning, isCreateAction })
        : saving || isActioning;

    return (
        <AdminPage
            breadcrumbs={breadcrumbs}
            pageId={pageId}
            actions={
                <Actions>
                    {actionButtonsFn({
                        showDataTableActionButton,
                        isActioning,
                        recordType,
                        formData,
                        handleConfirmedDataTableAction,
                        handleSubmit,
                        handleSubmitFunction,
                        saveDisabled,
                        setError,
                    })}
                    {showSaveButton && (
                        <Button
                            onClick={handleSubmit(handleSubmitFunction)}
                            disabled={saveDisabled}
                            variant="contained"
                            color="primary"
                        >
                            <Trans>Save</Trans>
                        </Button>
                    )}
                </Actions>
            }
        >
            <FormProvider {...methods}>
                <NgpForm
                    submitFunction={handleSubmitFunction}
                    initialized={initialized || saving || dataTableActioning}
                    loading={finding}
                    isDirtyFunc={dirtyCheck}
                    focusFirstField={false}
                    saveButtonDisabled={saveDisabled}
                    showSave={showSave}
                >
                    <RecordContextProviderUtilities id={recordId} type={recordType}>
                        <NgpFormContextProviderUtilities formData={formData}>
                            {typeof children === 'function'
                                ? children({
                                      formData,
                                      recordType,
                                      isCreateAction,
                                      handleSubmit,
                                      handleSubmitFunction,
                                      saveDisabled,
                                      isActioning,
                                  })
                                : children}
                        </NgpFormContextProviderUtilities>
                    </RecordContextProviderUtilities>
                </NgpForm>
            </FormProvider>
        </AdminPage>
    );
}

InfrastructureFormDetailBase.propTypes = {
    recordId: PropTypes.string,
    showBreadcrumb: PropTypes.bool,
    showSaveButton: PropTypes.bool,
    showSave: PropTypes.bool,
    tableSelectionAction: PropTypes.bool,
    baseUrl: PropTypes.string.isRequired,
    goToUrl: PropTypes.string.isRequired,
    defaultValues: PropTypes.object,
    pageId: PropTypes.string.isRequired,
    nameField: PropTypes.string,
    typeField: PropTypes.string,
    newRecordBreadcrumbText: PropTypes.string,
    functionBreadcrumbs: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string,
            url: PropTypes.string,
        })
    ),
    actionUrls: PropTypes.objectOf(PropTypes.string),
    createSubmitTransform: PropTypes.func,
    submitTransform: PropTypes.func,
    getTargetConfig: PropTypes.func,
    isSaveDisabled: PropTypes.func,
    actionButtonsFn: PropTypes.func.isRequired,
    setTestTypeId: PropTypes.func,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
};

InfrastructureFormDetailBase.defaultProps = {
    recordId: '',
    showBreadcrumb: true,
    showSaveButton: true,
    showSave: true,
    tableSelectionAction: false,
    defaultValues: {},
    nameField: 'name',
    typeField: 'type',
    newRecordBreadcrumbText: '',
    functionBreadcrumbs: [],
    actionUrls: {},
    createSubmitTransform: undefined,
    submitTransform: undefined,
    getTargetConfig: undefined,
    isSaveDisabled: undefined,
    setTestTypeId: undefined,
};

/**
 * Wrapper component that provides ActioningProvider context
 * This is the public API component
 */
export default function InfrastructureDetailBase(props) {
    return (
        <ActioningProviderUtilities>
            <InfrastructureFormDetailBase {...props} />
        </ActioningProviderUtilities>
    );
}

InfrastructureDetailBase.propTypes = {
    recordId: PropTypes.string,
    showBreadcrumb: PropTypes.bool,
    showSaveButton: PropTypes.bool,
    tableSelectionAction: PropTypes.bool,
    baseUrl: PropTypes.string.isRequired,
    goToUrl: PropTypes.string.isRequired,
    defaultValues: PropTypes.object,
    pageId: PropTypes.string.isRequired,
    nameField: PropTypes.string,
    typeField: PropTypes.string,
    newRecordBreadcrumbText: PropTypes.string,
    functionBreadcrumbs: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string,
            link: PropTypes.string,
        })
    ),
    actionUrls: PropTypes.objectOf(PropTypes.string),
    createSubmitTransform: PropTypes.func,
    submitTransform: PropTypes.func,
    getTargetConfig: PropTypes.func,
    isSaveDisabled: PropTypes.func,
    actionButtonsFn: PropTypes.func.isRequired,
    setTestTypeId: PropTypes.func,
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]).isRequired,
};

InfrastructureDetailBase.defaultProps = {
    recordId: '',
    showBreadcrumb: true,
    showSaveButton: true,
    tableSelectionAction: false,
    defaultValues: {},
    nameField: 'name',
    typeField: 'type',
    newRecordBreadcrumbText: '',
    functionBreadcrumbs: [],
    actionUrls: {},
    createSubmitTransform: undefined,
    submitTransform: undefined,
    getTargetConfig: undefined,
    isSaveDisabled: undefined,
    setTestTypeId: undefined,
};
